/**
 * CampaignsView - Vista principal de gestión de campañas drip
 * Muestra lista de campañas con estadísticas y detalle
 */

import { useState, useEffect } from 'react'
import { campaignsAPI, adminAPI } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import CampaignDetailView from './CampaignDetailView'
import CalendarView from './CalendarView'
import ActiveCohortsView from './ActiveCohortsView'
import StepHistoryView from './StepHistoryView'
import InstantModeSwitch from './InstantModeSwitch'
import KeywordTriggerControl from './KeywordTriggerControl'

export default function CampaignsView() {
  const { user } = useAuth()
  const isAdmin = user?.is_admin === true

  // Estado
  const [campaigns, setCampaigns] = useState([])
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [activeTab, setActiveTab] = useState('list')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newCampaignName, setNewCampaignName] = useState('')
  const [creating, setCreating] = useState(false)
  const [categories, setCategories] = useState([])
  const [newCampaignCategory, setNewCampaignCategory] = useState('')

  // Cargar campañas al montar
  useEffect(() => {
    loadCampaigns()
    if (isAdmin) adminAPI.getCampaignCategories().then(d => setCategories(d.categories || [])).catch(() => {})
  }, [])

  // silent=true refresca la lista sin el spinner de pantalla completa
  // (usado para reconciliacion tras editar/duplicar/borrar — evita parpadeo).
  const loadCampaigns = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      const data = await campaignsAPI.getCampaigns()
      setCampaigns(data.campaigns || [])
    } catch (err) {
      console.error('Error cargando campañas:', err)
      if (!silent) setError('Error al cargar campañas')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const handleSelectCampaign = async (campaign) => {
    try {
      const data = await campaignsAPI.getCampaign(campaign.id)
      setSelectedCampaign(data.campaign)
    } catch (err) {
      console.error('Error cargando campaña:', err)
    }
  }

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim()) return
    try {
      setCreating(true)
      await campaignsAPI.createCampaign({
        name: newCampaignName.trim(),
        campaign_type: 'drip',
        category_id: newCampaignCategory || null
      })
      setNewCampaignName('')
      setNewCampaignCategory('')
      setShowCreateModal(false)
      await loadCampaigns()
    } catch (err) {
      console.error('Error creando campaña:', err)
      alert('Error al crear campaña')
    } finally {
      setCreating(false)
    }
  }

  const handleSetCategory = async (campaign, categoryId) => {
    try {
      await campaignsAPI.updateCampaign(campaign.id, { category_id: categoryId || null })
      setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, category_id: categoryId || null } : c))
      if (selectedCampaign?.id === campaign.id) setSelectedCampaign(sc => sc ? { ...sc, category_id: categoryId || null } : sc)
    } catch (err) {
      console.error('Error asignando categoria:', err)
      alert('Error al asignar categoría')
    }
  }

  const handleDeleteCampaign = async (campaignId) => {
    if (!confirm('¿Eliminar esta campaña? Esta acción no se puede deshacer.')) return
    try {
      await campaignsAPI.deleteCampaign(campaignId)
      if (selectedCampaign?.id === campaignId) {
        setSelectedCampaign(null)
      }
      // Optimista: quitar la tarjeta al instante + reconciliar en silencio
      setCampaigns(prev => prev.filter(c => c.id !== campaignId))
      loadCampaigns({ silent: true })
    } catch (err) {
      const status = err.response?.status
      const data = err.response?.data
      if (status === 409 && typeof data?.activeCohorts === 'number') {
        alert(`No se puede eliminar: la campaña tiene ${data.activeCohorts} cohort(s) en ejecución. Cancélalas primero desde la pestaña "En curso".`)
      } else if (status === 404) {
        if (selectedCampaign?.id === campaignId) setSelectedCampaign(null)
        alert('La campaña ya no existe')
        await loadCampaigns()
      } else {
        console.error('Error eliminando campaña:', err)
        alert('Error al eliminar campaña')
      }
    }
  }

  const handleCampaignUpdate = (updatedCampaign = null) => {
    // Optimista: refleja el cambio (titulo/descripcion/estado) en la tarjeta
    // izquierda y en el detalle al instante, sin esperar el refetch.
    if (updatedCampaign?.id) {
      setCampaigns(prev => prev.map(c =>
        c.id === updatedCampaign.id ? { ...c, ...updatedCampaign } : c
      ))
      setSelectedCampaign(prev =>
        prev?.id === updatedCampaign.id ? { ...prev, ...updatedCampaign } : prev
      )
    }
    // Reconciliacion silenciosa con backend (sin spinner): nuevas campañas
    // (duplicar), counts y steps actualizados.
    loadCampaigns({ silent: true })
    if (selectedCampaign?.id) {
      // Refetch del detalle (trae steps frescos). Pasar solo el id evita cualquier
      // riesgo de usar un objeto stale: handleSelectCampaign solo usa .id.
      handleSelectCampaign({ id: selectedCampaign.id })
    }
  }

  // Modo instantáneo: confirmado por el doble-click del InstantModeSwitch (tapa + switch)
  const [instantBusy, setInstantBusy] = useState(null) // campaign.id con request en vuelo
  const handleInstantToggle = async (campaign, next) => {
    setInstantBusy(campaign.id)
    try {
      const res = await campaignsAPI.setInstantMode(campaign.id, next)
      handleCampaignUpdate(res?.campaign || { id: campaign.id, instant_mode: next })
    } catch (e) {
      console.error('Error al cambiar modo instantáneo:', e)
      alert('Error al cambiar el modo instantáneo')
    } finally {
      setInstantBusy(null)
    }
  }

  // Trigger por keyword en la Nota Express (admin): confirmado desde el KeywordTriggerControl
  const [kwBusy, setKwBusy] = useState(null) // campaign.id con request en vuelo
  const handleKeywordSave = async (campaign, cfg) => {
    setKwBusy(campaign.id)
    try {
      const res = await campaignsAPI.setKeywordTrigger(campaign.id, cfg)
      handleCampaignUpdate(res?.campaign || { id: campaign.id, trigger_enabled: cfg.enabled, trigger_keyword: cfg.keyword, trigger_match_mode: cfg.matchMode })
    } catch (e) {
      console.error('Error guardando trigger keyword:', e)
      alert('Error al guardar el disparador')
    } finally {
      setKwBusy(null)
    }
  }

  const getStatusBadge = (status) => {
    // Solo 3 status válidos para campaña madre (reutilizable).
    // El lifecycle running/completed/cancelled corresponde a cohortes.
    const config = {
      draft: { bg: 'bg-gray-100 dark:bg-slate-700/50', text: 'text-gray-600 dark:text-gray-300', border: 'border-gray-200 dark:border-slate-600', dot: 'bg-gray-400 dark:bg-gray-500', label: 'Borrador' },
      active: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800', dot: 'bg-emerald-500', label: 'Activa' },
      archived: { bg: 'bg-slate-100 dark:bg-slate-700/50', text: 'text-slate-600 dark:text-slate-300', border: 'border-slate-300 dark:border-slate-600', dot: 'bg-slate-400 dark:bg-slate-500', label: 'Archivada' }
    }
    const c = config[status] || config.draft
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {c.label}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full flex-1 bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-3"></div>
          <p className="text-gray-500">Cargando campanas...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-1">
      {/* Panel izquierdo: Lista de campañas - Estilo Koomo (1/3 del area) */}
      <div className="w-1/3 flex-shrink-0 border-r border-gray-200 bg-gray-100 flex flex-col">
        <div className="p-4 border-b border-gray-200 bg-white flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Campanas</h2>
            <p className="text-xs text-gray-500">{campaigns.length} campanas</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-9 h-9 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl flex items-center justify-center hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg"
              title="Nueva campana"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>

        {/* Lista de campañas - Cards estilo Koomo */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {error && (
            <div className="p-4 bg-red-50 text-red-600 text-sm rounded-xl">{error}</div>
          )}

          {campaigns.length === 0 ? (
            <div className="p-6 text-center text-gray-500 bg-white rounded-xl shadow-sm">
              <span className="text-4xl mb-3 block">📢</span>
              <p>No hay campanas creadas</p>
              {isAdmin && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-3 text-primary-600 hover:text-primary-700 font-medium text-sm"
                >
                  Crear primera campana
                </button>
              )}
            </div>
          ) : (
            campaigns.map((campaign) => (
              <div
                key={campaign.id}
                onClick={() => handleSelectCampaign(campaign)}
                className={`
                  bg-white rounded-xl p-4 cursor-pointer transition-all duration-200
                  ${selectedCampaign?.id === campaign.id
                    ? 'shadow-md ring-2 ring-primary-500 ring-opacity-50'
                    : 'shadow-sm hover:shadow-md border border-gray-100 hover:border-gray-200'
                  }
                `}
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-900 truncate flex-1 pr-2">{campaign.name}</h3>
                  {getStatusBadge(campaign.status)}
                </div>
                {campaign.description && (
                  <p className="text-xs text-gray-500 truncate mb-3">{campaign.description}</p>
                )}
                {/* Capsulas de contadores: inscritos / respondieron / cohortes en curso */}
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800"
                    title="Prospectos inscritos"
                  >
                    <span>👥</span>
                    <span>{campaign.enrolled_count ?? 0}</span>
                    <span className="text-blue-400 dark:text-blue-400/70">inscritos</span>
                  </span>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800"
                    title="Contactos que respondieron"
                  >
                    <span>💬</span>
                    <span>{campaign.responded_count ?? 0}</span>
                    <span className="text-emerald-400 dark:text-emerald-400/70">resp.</span>
                  </span>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800"
                    title="Cohortes en curso (recolectando / bloqueadas / corriendo)"
                  >
                    <span>🚀</span>
                    <span>{campaign.active_cohorts_count ?? 0}</span>
                    <span className="text-purple-400 dark:text-purple-400/70">en curso</span>
                  </span>
                </div>
                {isAdmin && categories.length > 0 && (
                  <div className="mb-2.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-gray-300 dark:border-gray-600" style={{ backgroundColor: categories.find(c => c.id === campaign.category_id)?.color || 'transparent' }} title="Color de la categoría" />
                    <select
                      value={campaign.category_id || ''}
                      onChange={(e) => handleSetCategory(campaign, e.target.value)}
                      className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                      title="Categoría de la campaña"
                    >
                      <option value="">Sin categoría</option>
                      {categories.filter(c => c.is_active || c.id === campaign.category_id).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-gray-400" title="Fecha de creacion">
                    Creación: {new Date(campaign.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <KeywordTriggerControl
                        campaign={campaign}
                        busy={kwBusy === campaign.id}
                        onSave={(cfg) => handleKeywordSave(campaign, cfg)}
                      />
                    )}
                    {!isAdmin && campaign.trigger_enabled === true && (
                      <span title={campaign.trigger_keyword} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-sky-100 border border-sky-400 text-sky-700 text-xs font-medium dark:bg-sky-900/30 dark:border-sky-600 dark:text-sky-300 max-w-[140px]">🔑 <span className="truncate">{campaign.trigger_keyword}</span></span>
                    )}
                    {isAdmin ? (
                      <InstantModeSwitch
                        enabled={campaign.instant_mode === true}
                        busy={instantBusy === campaign.id}
                        onToggle={(next) => handleInstantToggle(campaign, next)}
                      />
                    ) : campaign.instant_mode === true && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 border border-amber-400 text-amber-700 text-xs font-medium dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-300">
                        ⚡ Instantánea
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Panel central/derecho: Tabs + contenido */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50">
        {/* Tab bar */}
        <div className="flex gap-2 px-6 pt-4 border-b border-gray-200 bg-white flex-shrink-0">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'list' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Detalle
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'calendar' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Calendario
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'active' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            En curso
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'history' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Historial
          </button>
        </div>

        {/* Tab content */}
        {activeTab === 'calendar' ? (
          <CalendarView isAdmin={isAdmin} />
        ) : activeTab === 'active' ? (
          <ActiveCohortsView isAdmin={isAdmin} />
        ) : activeTab === 'history' ? (
          <StepHistoryView isAdmin={isAdmin} />
        ) : (
          <>
            {selectedCampaign ? (
              <CampaignDetailView
                campaign={selectedCampaign}
                isAdmin={isAdmin}
                onUpdate={handleCampaignUpdate}
                onDelete={() => handleDeleteCampaign(selectedCampaign.id)}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center bg-white rounded-2xl p-8 shadow-sm max-w-sm">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">📢</span>
                  </div>
                  <p className="text-lg font-medium text-gray-700 mb-1">Selecciona una campana</p>
                  <p className="text-sm text-gray-400">para ver sus detalles y configuracion</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal crear campaña - Estilo Koomo */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header con gradiente */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
              <h3 className="text-lg font-semibold text-white">Nueva Campana</h3>
              <p className="text-primary-100 text-sm">Crea una campana de mensajes automaticos</p>
            </div>

            {/* Body */}
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de la campana
              </label>
              <input
                type="text"
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                placeholder="Ej: Seguimiento Enero 2025"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCampaign()}
              />
              {isAdmin && categories.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categoría (opcional)</label>
                  <select
                    value={newCampaignCategory}
                    onChange={(e) => setNewCampaignCategory(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
                  >
                    <option value="">Sin categoría</option>
                    {categories.filter(c => c.is_active).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl transition font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={!newCampaignName.trim() || creating}
                className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition disabled:opacity-50 font-medium shadow-md"
              >
                {creating ? 'Creando...' : 'Crear Campana'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
